/**
 * used to avoid tiny gaps at the start and end that we assume were intended to be clipped
 */
const START_END_CLIP_BUFFER = 0.1;
const DISTORTION_CLIPPING_THRESHOLD = 0.95;

type SimpleRegion = {
  start: number;
  end: number;
};

/**
 * creates non-overlapping regions in ascending order.
 *
 * START_END_CLIP_BUFFER is used to ensure that tiny gaps after the start and before the end of the audio clip are also
 * removed.
 */
const normalizeRegions = (
  regions: Readonly<SimpleRegion[]>,
  totalAudioDuration: number,
): SimpleRegion[] => {
  /**
   * stack of regions in reverse order (so popping gets the next region in ascending order)
   *
   * nonsense data is removed
   */
  const regionStack = regions
    .map(r => ({
      start: Math.max(0, r.start),
      end: Math.min(totalAudioDuration, r.end),
    }))
    .filter(r => r.start < r.end)
    .sort((a, b) => b.start - a.start);

  const res: SimpleRegion[] = [];

  let region: SimpleRegion | undefined;
  while ((region = regionStack.pop()) !== undefined) {
    let { end } = region;
    while (regionStack.length > 0 && regionStack[regionStack.length - 1].start <= end) {
      end = Math.max(end, regionStack[regionStack.length - 1].end);
      regionStack.pop();
    }
    res.push({ start: region.start, end });
  }

  // return early if there are no regions to prevent unsafe access
  if (res.length === 0) {
    return [];
  }

  // adjust the start of the first region and end of the last region to remove tiny gaps
  if (res[0].start < START_END_CLIP_BUFFER) {
    res[0].start = 0;
  }
  if (totalAudioDuration - res[res.length - 1].end < START_END_CLIP_BUFFER) {
    res[res.length - 1].end = totalAudioDuration;
  }

  return res;
};

/**
 * returns null if there are no regions to clip
 */
export const clipRegionsOutOfAudioBuffer = async ({
  audioContext,
  originalBuffer,
  regions,
}: Readonly<{
  audioContext: AudioContext;
  originalBuffer: AudioBuffer;
  regions: SimpleRegion[];
}>): Promise<AudioBuffer | null> => {
  const normalizedRegions = normalizeRegions(regions, originalBuffer.duration);

  // do nothing if there are no regions
  if (normalizedRegions.length === 0) {
    return null;
  }

  // Calculate the total remaining duration by subtracting region durations from the original
  const remainingDuration = normalizedRegions.reduce(
    (acc, r) => acc - (r.end - r.start),
    originalBuffer.duration,
  );

  // Create new buffer for clipped audio
  const newBuffer = audioContext.createBuffer(
    originalBuffer.numberOfChannels,
    Math.floor(remainingDuration * originalBuffer.sampleRate),
    originalBuffer.sampleRate,
  );

  for (let channelIndex = 0; channelIndex < originalBuffer.numberOfChannels; channelIndex++) {
    // Get the channel data from the original and new buffers
    const originalChannelData = originalBuffer.getChannelData(channelIndex);
    const newChannelData = newBuffer.getChannelData(channelIndex);
    /**
     * controls where to write into newChannelData
     */
    let newChannelDataWritePointer = 0;

    // write audio before start of first region to newChannelData
    if (normalizedRegions[0].start > 0) {
      // get the minimum of these values to prevent unsafe reads / writes
      const sampleLength = Math.min(
        // desired length (next start * sample rate)
        Math.floor(normalizedRegions[0].start * originalBuffer.sampleRate),
        // space left to read from originalChannelData
        originalChannelData.length,
        // space left to write to newChannelData
        newChannelData.length,
      );

      if (sampleLength > 0) {
        newChannelData.set(originalChannelData.subarray(0, sampleLength));
        newChannelDataWritePointer = sampleLength;
      }
    }

    // for each region, write the audio between the end of the previous region and the start of the current region
    for (let regionIndex = 1; regionIndex < normalizedRegions.length; regionIndex++) {
      const startSample = Math.floor(
        normalizedRegions[regionIndex - 1].end * originalBuffer.sampleRate,
      );

      const sampleLength = Math.min(
        // desired length (next start * sample rate - startSample)
        Math.floor(normalizedRegions[regionIndex].start * originalBuffer.sampleRate) - startSample,
        // space left to read from originalChannelData
        originalChannelData.length - startSample,
        // space left to write to newChannelData
        newChannelData.length - newChannelDataWritePointer,
      );

      if (sampleLength > 0) {
        newChannelData.set(
          originalChannelData.subarray(startSample, startSample + sampleLength),
          newChannelDataWritePointer,
        );
        newChannelDataWritePointer += sampleLength;
      }
    }

    // write audio after end of last region to newChannelData
    if (normalizedRegions[normalizedRegions.length - 1].end < originalBuffer.duration) {
      const startSample = Math.floor(
        normalizedRegions[normalizedRegions.length - 1].end * originalBuffer.sampleRate,
      );

      const sampleLength = Math.min(
        // desired length (end of original channel data - startSample)
        originalChannelData.length - startSample,
        // space left to write to newChannelData
        newChannelData.length - newChannelDataWritePointer,
      );

      if (sampleLength > 0) {
        newChannelData.set(
          originalChannelData.subarray(startSample, startSample + sampleLength),
          newChannelDataWritePointer,
        );
      }
    }
  }

  return newBuffer;
};

/**
 * used to compute color values since wave surfer uses a detached head i think
 */
export const getColorFromNode = ({
  node,
  colorVarName,
  opacity,
}: {
  node: HTMLElement | null;
  colorVarName: string;
  opacity: number;
}): string | undefined => {
  if (node === null) {
    return undefined;
  }

  const color = getComputedStyle(node).getPropertyValue(colorVarName);
  return `rgb(${color} / ${opacity})`;
};

/** used to convert raw values to decibels and normalize them on a logarithmic scale */
const valToDb = (v: number) => {
  return 20 * Math.log10(Math.abs(v) + 1e-10);
};

export const valToDbNormalized = (v: number) => {
  const decibels = valToDb(v);
  const normDb = decibels < -60 ? 0 : (decibels + 60) / 60;
  return normDb;
};

/** Render normalized waveform - assume mono audio
 *
 * Also returns any validation errors that are computed from waveform analysis on the frontend (as opposed to other audio computation we do asynchronously
 * on the backend).
 */

export interface AudioValidationError {
  message: string;
}
export const renderNormalizedWaveform = (
  wavesurfer: any,
  canvas: any,
  waveColor?: string,
): Array<AudioValidationError> => {
  const ctx = canvas.getContext('2d');

  // Configure the canvas
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  // Use logical dimensions for drawing - offset width to account for drawing the vertical scale
  ctx.clearRect(0, 0, rect.width, rect.height);
  const height = rect.height;
  const width = rect.width;

  // Check if the buffer is available
  if (!wavesurfer.backend.buffer) {
    return [{ message: 'Audio buffer is not available.' }];
  }

  // Calculate how many audio samples correspond to one pixel and account for zoom level
  const channelData = wavesurfer.backend.buffer.getChannelData(0);
  const samplesPerPixel = channelData.length / width;

  ctx.save();
  ctx.translate(0, height / 2);

  let areAnySamplesClipped = false;
  // Iterate over each pixel in the width
  for (let canvasIdx = 0; canvasIdx < width; canvasIdx++) {
    ctx.beginPath();

    const index = Math.floor(canvasIdx * samplesPerPixel);

    // Aggregate values for a window of samples since there is more than one sample at each pixel
    let value = 0;
    let nvalue = 0;
    const windowSize = Math.max(1, samplesPerPixel);
    for (let i = index; i < index + windowSize && i < channelData.length; i++) {
      const v = channelData[i];
      value = Math.max(value, v);
      nvalue = Math.min(nvalue, v);
    }

    // Detect clipping
    const isClipped =
      value >= DISTORTION_CLIPPING_THRESHOLD || nvalue <= -DISTORTION_CLIPPING_THRESHOLD;
    areAnySamplesClipped = isClipped ? true : areAnySamplesClipped;

    ctx.strokeStyle = waveColor;

    // Normalize the values and draw the waveform
    value = valToDbNormalized(value);
    nvalue = -valToDbNormalized(nvalue);

    const y = (-value * height) / 2;

    ctx.moveTo(canvasIdx, 0);
    ctx.lineTo(canvasIdx, y);
    ctx.lineTo(canvasIdx + 1, 0);

    const yNeg = (-nvalue * height) / 2;

    ctx.moveTo(canvasIdx + 1, 0);
    ctx.lineTo(canvasIdx + 1, yNeg);
    ctx.lineTo(canvasIdx + 2, 0);

    ctx.stroke();
    ctx.closePath();
  }
  ctx.restore();

  if (areAnySamplesClipped) {
    return [
      {
        message:
          'Detected clipping in the red segments - this means there was a sudden rush of input into the mic that was more than your recording device could handle, and some of the audio you recorded was lost. To fix this, you can try moving to the side or adding a pop filter.',
      },
    ];
  }
  return [];
};
